/** Public Privacy Policy. Companion to Terms of Service. */

import { Link } from "react-router-dom";
import { LegalShell, P, Section, Ul } from "./TermsPage";

export const PRIVACY_VERSION = "2026-04-27b";

export function PrivacyPage() {
  return (
    <LegalShell title="Privacy Policy" version={PRIVACY_VERSION}>
      <Section title="Who we are">
        <P>
          PencilOut is operated by <strong>Bramar Strategic Services LLC</strong>, a Charlotte-area
          software company. For privacy questions, data export requests, or account deletions email{" "}
          <a href="mailto:support@pencilout.app" className="underline">
            support@pencilout.app
          </a>{" "}
          and reference this policy.
        </P>
      </Section>
      <Section title="What we collect">
        <P>
          When you create a PencilOut account we collect: your name, email address, company name,
          and IP address at signup time. When you use the service we collect: walkthrough videos you
          upload, job and client details you enter, quote line items you create or that the AI
          generates, comments you exchange with your clients, and the IP address + user agent of any
          client who signs a quote (audit trail).
        </P>
        <P>
          For billing we collect: invoice records, payment status, and the subscription tier you're
          on. Card numbers are NOT stored by us — Stripe handles them.
        </P>
      </Section>

      <Section title="How we use it">
        <P>We use your data only to:</P>
        <Ul>
          <li>Operate the service you're paying for.</li>
          <li>Process walkthroughs into draft quotes.</li>
          <li>Send transactional emails (sign-in links, quote notifications, billing).</li>
          <li>Bill your subscription and any overage quotes.</li>
          <li>Investigate suspected abuse (security monitoring, fraud prevention).</li>
          <li>Improve the product through aggregate, non-identifying analytics.</li>
        </Ul>
        <P>
          We do <strong>not</strong> use your customer data to train AI models, sell it to third
          parties, or use it for cross-context behavioral advertising.
        </P>
      </Section>

      <Section title="Subprocessors">
        <P>We share data with these third-party vendors strictly to operate the service:</P>
        <Ul>
          <li>
            <strong>AI processing providers</strong> — we send your walkthrough video +
            scope-extraction prompt to a third-party AI provider for transcription and quote
            generation. The current providers (subject to change as the AI landscape evolves)
            contractually agree not to use API content for model training. Available on request:{" "}
            <a href="mailto:support@pencilout.app" className="underline">
              email support
            </a>
            .
          </li>
          <li>
            <strong>Stripe</strong> — receives your business contact info (name, email, company) and
            processes invoices + payments. Stripe's policy:{" "}
            <a
              href="https://stripe.com/privacy"
              className="underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              stripe.com/privacy
            </a>
            .
          </li>
          <li>
            <strong>Resend</strong> — sends our transactional emails (sign-in links, quote
            notifications). Receives your email address + the email body. Policy:{" "}
            <a
              href="https://resend.com/legal/privacy-policy"
              className="underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              resend.com/legal/privacy-policy
            </a>
            .
          </li>
          <li>
            <strong>Cloudflare Pages</strong> — hosts the admin web app. Cloudflare's terms:{" "}
            <a
              href="https://www.cloudflare.com/privacypolicy/"
              className="underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              cloudflare.com/privacypolicy
            </a>
            .
          </li>
          <li>
            <strong>Amazon Lightsail</strong> — hosts the API + database. AWS's terms:{" "}
            <a
              href="https://aws.amazon.com/privacy/"
              className="underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              aws.amazon.com/privacy
            </a>
            .
          </li>
          <li>
            <strong>Sentry</strong> — receives error reports from the API + admin web app. We do not
            send PII; we strip request bodies.
          </li>
        </Ul>
        <P>
          We don't use any other data processors. New subprocessors are announced via email + an
          updated version of this policy before they go live.
        </P>
      </Section>

      <Section title="How we secure it">
        <P>
          The database is private (not exposed to the public internet). Sensitive credentials
          (Stripe keys, Resend keys) are encrypted at rest with Fernet. Sign-in uses email
          magic-links — no passwords are stored. All HTTP traffic is TLS-encrypted (Caddy +
          Cloudflare). Walkthrough videos are stored on the API host's encrypted disk.
        </P>
      </Section>

      <Section title="Retention">
        <P>
          Active customer data is retained for as long as your account is active. Deleted accounts:
          walkthrough videos and quote drafts are deleted within 30 days. AI cost events and billing
          records are retained 7 years for tax/audit reasons. Sentry error reports auto-expire per
          Sentry's retention (typically 30–90 days).
        </P>
      </Section>

      <Section title="Your rights">
        <P>You can:</P>
        <Ul>
          <li>
            <strong>Access</strong> all your data via the admin UI or by requesting an export at{" "}
            <a href="mailto:support@pencilout.app" className="underline">
              support@pencilout.app
            </a>
            .
          </li>
          <li>
            <strong>Correct</strong> any inaccurate information from the admin UI; we'll honor a
            request via support if you can't edit something directly.
          </li>
          <li>
            <strong>Delete</strong> your account from the admin UI or by email. Deletion is
            irrevocable; export anything you need first.
          </li>
          <li>
            <strong>Object</strong> to processing in specific circumstances. For non-essential
            processing (analytics) we'll honor your request; for essential processing (billing, AI
            quote generation) we may need to terminate the relationship instead.
          </li>
        </Ul>
        <P>
          California residents have additional rights under CCPA; EU/UK residents under GDPR. We
          honor those rights without geographic discrimination — same options for everyone.
        </P>
      </Section>

      <Section title="Cookies">
        <P>
          The admin web app uses two cookies: a session cookie (HttpOnly, Secure, SameSite=Lax) for
          auth, and an anti-CSRF double-submit token. We don't use third-party tracking cookies or
          advertising pixels.
        </P>
      </Section>

      <Section title="Children">
        <P>
          PencilOut is for adult contractors managing their business. We don't knowingly collect
          data from anyone under 18. If you believe we have, contact us and we'll delete it.
        </P>
      </Section>

      <Section title="Changes">
        <P>
          We update this policy when our processing changes. The version date at the top reflects
          the last substantive update. Material changes are announced via email + in-app banner
          before they take effect. The full revision history is in our public repo.
        </P>
      </Section>

      <Section title="Contact">
        <P>
          Privacy questions, data requests, or complaints:{" "}
          <a href="mailto:support@pencilout.app" className="underline">
            support@pencilout.app
          </a>
          . See also our{" "}
          <Link to="/terms" className="underline">
            Terms of Service
          </Link>
          .
        </P>
      </Section>
    </LegalShell>
  );
}
